import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import CrudFormModal, { type FormField } from "./CrudFormModal";

export type { FormField };

export interface Column<T> {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
  render?: (row: T) => ReactNode;
}

export interface RowAction<T> {
  label: string | ((row: T) => string);
  color?: string | ((row: T) => string);
  onClick: (row: T) => Promise<void> | void;
}

export interface PageResult<T> {
  items: T[];
  total: number;
}

interface CrudListPageProps<T extends { id: string }> {
  title: string;
  columns: Column<T>[];
  fetchPage: (page: number, size: number) => Promise<PageResult<T>>;
  searchKeys?: (keyof T)[];
  actions?: RowAction<T>[];
  rowSx?: (row: T) => object;
  createLabel?: string;
  createFields?: FormField[];
  onSubmitCreate?: (data: Record<string, string>) => Promise<void>;
}

function CrudListPage<T extends { id: string }>({
  title,
  columns,
  fetchPage,
  searchKeys = [],
  actions = [],
  rowSx,
  createLabel = "Nuevo",
  createFields,
  onSubmitCreate,
}: CrudListPageProps<T>) {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(
    async (p: number, s: number) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchPage(p, s);
        setItems(result.items);
        setTotal(result.total);
      } catch {
        setError("No se pudo cargar los datos.");
      } finally {
        setLoading(false);
      }
    },
    [fetchPage],
  );

  useEffect(() => {
    load(page, size);
  }, [page, size, load]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((row) =>
      searchKeys.some((k) => String(row[k] ?? "").toLowerCase().includes(q)),
    );
  }, [items, search, searchKeys]);

  const totalPages = Math.ceil(total / size);

  const handleAction = async (action: RowAction<T>, row: T) => {
    setActionLoading(row.id);
    try {
      await action.onClick(row);
      await load(page, size);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreated = async (data: Record<string, string>) => {
    await onSubmitCreate!(data);
    await load(page, size);
  };

  const getLabel = (action: RowAction<T>, row: T) =>
    typeof action.label === "function" ? action.label(row) : action.label;

  const getColor = (action: RowAction<T>, row: T) =>
    typeof action.color === "function" ? action.color(row) : (action.color ?? "var(--color-accent)");

  const canCreate = !!createFields && !!onSubmitCreate;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {title}
        </Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModalOpen(true)} disableElevation>
            {createLabel}
          </Button>
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 320 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ py: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && (
        <>
          <Box
            sx={{
              borderRadius: 2,
              border: "1px solid var(--color-border)",
              overflow: "hidden",
              bgcolor: "var(--color-surface)",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "var(--color-surface-raised)" }}>
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align} sx={{ fontWeight: 600 }}>
                      {col.label}
                    </TableCell>
                  ))}
                  {actions.length > 0 && (
                    <TableCell sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + (actions.length > 0 ? 1 : 0)}
                      align="center"
                      sx={{ py: 4, color: "var(--color-text-secondary)" }}
                    >
                      No se encontraron resultados.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ "&:last-child td": { border: 0 }, ...(rowSx?.(row) ?? {}) }}
                    >
                      {columns.map((col) => (
                        <TableCell key={col.key} align={col.align}>
                          {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? "")}
                        </TableCell>
                      ))}
                      {actions.length > 0 && (
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {actions.map((action) => (
                              <Button
                                key={getLabel(action, row)}
                                size="small"
                                disabled={actionLoading === row.id}
                                onClick={() => handleAction(action, row)}
                                sx={{
                                  fontSize: 12,
                                  color: getColor(action, row),
                                  minWidth: "unset",
                                  textTransform: "none",
                                }}
                              >
                                {getLabel(action, row)}
                              </Button>
                            ))}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Filas por página:
              </Typography>
              <Select
                size="small"
                value={size}
                onChange={(e) => {
                  setSize(Number(e.target.value));
                  setPage(1);
                }}
                sx={{ fontSize: 14 }}
              >
                {[10, 20, 50].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="body2" color="text.secondary">
                {total} resultados en total
              </Typography>
            </Box>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              shape="rounded"
              size="small"
              color="primary"
            />
          </Box>
        </>
      )}

      {canCreate && (
        <CrudFormModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleCreated}
          title={createLabel}
          fields={createFields!}
          submitLabel="Crear"
        />
      )}
    </Box>
  );
}

export default CrudListPage;
